import {
  FramebufferTexture,
  GLSL3,
  Mesh,
  ShaderMaterial,
  PlaneGeometry,
  Scene,
  Vector2,
  type Camera,
  type WebGLRenderer,
} from 'three';
import { smoothstep } from '../../core/math';
import fullscreenVert from '../shaders/fullscreen.vert?raw';
import handoffFrag from '../shaders/painterHandoff.frag?raw';

const DISSOLVE_MS = 400;

/**
 * Copying the finished frame on the GPU keeps the painting's appearance without a CPU readback
 * or another world render on every frame of the dissolve.
 */
export class PainterHandoff {
  readonly scene = new Scene();
  private texture = new FramebufferTexture(1, 1);
  private readonly uniforms = {
    tFrame: { value: this.texture },
    uOpacity: { value: 0 },
  };
  private readonly material = new ShaderMaterial({
    uniforms: this.uniforms,
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: handoffFrag,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly quad = new Mesh(new PlaneGeometry(2, 2), this.material);
  private readonly size = new Vector2();
  private started: number | null = null;
  private active = false;

  constructor() {
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  get opacity(): number {
    return this.uniforms.uOpacity.value;
  }

  capture(renderer: WebGLRenderer): void {
    renderer.getDrawingBufferSize(this.size);
    const image = this.texture.image;
    if (image.width !== this.size.x || image.height !== this.size.y) {
      this.texture.dispose();
      this.texture = new FramebufferTexture(this.size.x, this.size.y);
      this.uniforms.tFrame.value = this.texture;
    }
    renderer.copyFramebufferToTexture(this.texture);
    this.started = null;
    this.active = true;
    this.uniforms.uOpacity.value = 1;
  }

  draw(renderer: WebGLRenderer, camera: Camera, now: number): void {
    if (!this.active) return;
    this.started ??= now;
    this.uniforms.uOpacity.value = 1 - smoothstep(0, DISSOLVE_MS, now - this.started);
    if (this.opacity === 0) {
      this.active = false;
      return;
    }
    const autoClear = renderer.autoClear;
    try {
      renderer.autoClear = false;
      renderer.render(this.scene, camera);
    } finally {
      renderer.autoClear = autoClear;
    }
  }

  clear(): void {
    this.active = false;
    this.started = null;
    this.uniforms.uOpacity.value = 0;
  }

  dispose(): void {
    this.clear();
    this.texture.dispose();
    this.material.dispose();
    this.quad.geometry.dispose();
  }
}
